import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Keyboard } from "react-native";
import type { TamaguiElement } from "tamagui";
import { Calendar as CalendarIcon } from "@tamagui/lucide-icons";
import { dateTextToIso } from "../../utils/formatters";
import { DateField } from "../DateField";
import { Dropdown } from "../Dropdown";
import type { FieldValidation } from "../FieldShell";
import { Calendar } from "./Calendar";
const DISPLAY_FORMAT = "MM/DD/YYYY";
const ISO_FORMAT = "YYYY-MM-DD";
const formatDisplay = (date: Date): string => dayjs(date).format(DISPLAY_FORMAT);
const formatIso = (date: Date): string => dayjs(date).format(ISO_FORMAT);
const isoToDate = (iso: string): Date => dayjs(iso).toDate();
type DatePickerBaseProps = {
  /** Controlled selection. `null` is no date. Compared at day granularity. */
  value: Date | null;
  /** Fires the chosen day on a calendar pick or a complete valid typed date, and
   *  `null` when the field is cleared or holds an incomplete/invalid date. */
  onValueChange: (date: Date | null) => void;
  helperText?: string;
  validation?: FieldValidation;
  disabled?: boolean;
  required?: boolean;
  /** Inclusive lower bound; earlier days are non-selectable. */
  min?: Date;
  /** Inclusive upper bound; later days are non-selectable. */
  max?: Date;
  /** Predicate to disable arbitrary days (e.g. weekends). */
  isDateDisabled?: (date: Date) => boolean;
  /** 0 = Sunday (default), 1 = Monday. */
  weekStartsOn?: 0 | 1;
  /** Accessible label for the calendar surface. @default "Choose date" */
  contentLabel?: string;
  id?: string;
};

// Require at least one accessible name — a visible `label`, an `aria-label`, or
// both — so the field is never nameless to screen readers.
export type DatePickerProps = DatePickerBaseProps & ({
  label: string;
  "aria-label"?: string;
} | {
  label?: string;
  "aria-label": string;
});

/**
 * Controlled date-only input: a typeable masked `MM/DD/YYYY` `DateField` whose
 * trailing calendar button opens the calendar in `Dropdown`. Typing a complete
 * valid date or picking a day fires `onValueChange`; only the button opens it.
 */
export const DatePicker = ({
  value,
  onValueChange,
  label,
  helperText,
  validation = "none",
  disabled = false,
  required = false,
  min,
  max,
  isDateDisabled,
  weekStartsOn = 0,
  contentLabel = "Choose date",
  id,
  "aria-label": ariaLabel
}: DatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => value ? formatDisplay(value) : "");
  // Focus returns here when the calendar closes (the opener lives inside the field,
  // so `Dropdown` restores it via `returnFocusRef` rather than to the field itself).
  const calendarButtonRef = useRef<TamaguiElement | null>(null);
  // The roving-focus / displayed-month date for the calendar. Re-anchored to the
  // selection (or today) on each open; the calendar pages it from there.
  const [focusedDate, setFocusedDate] = useState<Date>(value ?? new Date());

  // The last ISO we emitted (typed or picked). Lets the reflect-effect tell our
  // own changes apart from a programmatic `value` swap, so it never overwrites a
  // partially-typed date the user is mid-way through.
  const lastEmittedIso = useRef<string | undefined>(value ? formatIso(value) : undefined);
  useEffect(() => {
    const valueIso = value ? formatIso(value) : undefined;
    if (valueIso !== lastEmittedIso.current) {
      lastEmittedIso.current = valueIso;
      setText(value ? formatDisplay(value) : "");
    }
  }, [value]);

  // `value` read through a ref so re-anchoring on open doesn't depend on it (a
  // parent re-render with the same instant must not snap away the user's paging).
  const valueRef = useRef(value);
  valueRef.current = value;
  const commit = useCallback((date: Date | null) => {
    // Dedup against the last committed value (and echoes of a reflected `value`).
    const iso = date ? formatIso(date) : undefined;
    if (iso === lastEmittedIso.current) {
      return;
    }
    lastEmittedIso.current = iso;
    onValueChange(date);
  }, [onValueChange]);
  const handleTextChange = useCallback((next: string) => {
    setText(next);
    // Commit as soon as a complete valid date is typed. An empty, incomplete, or
    // invalid field stays a draft — the committed value is preserved until blur,
    // so clearing to retype (or a typo) doesn't drop the last valid date.
    const iso = dateTextToIso(next);
    if (iso) {
      commit(isoToDate(iso));
    }
  }, [commit]);

  // Resolve the draft on blur: an empty field commits "no date"; an incomplete or
  // invalid draft reverts to the last committed value (or empty).
  const handleBlur = useCallback(() => {
    if (text === "") {
      commit(null);
    } else if (dateTextToIso(text) === undefined) {
      setText(value ? formatDisplay(value) : "");
    }
  }, [text, value, commit]);
  const handleSelect = useCallback((date: Date) => {
    setText(formatDisplay(date));
    commit(date);
    setOpen(false);
  }, [commit]);
  const handleCalendarPress = useCallback(() => {
    if (!disabled) {
      // Drop the field's keyboard first, or on native the calendar opens behind it.
      Keyboard.dismiss();
      // Re-anchor synchronously with opening so the calendar's focus-on-open lands
      // on the selection (or today), not the last-paged month.
      setFocusedDate(valueRef.current ?? new Date());
      setOpen(true);
    }
  }, [disabled]);
  const field = <DateField label={label} value={text} onValueChange={handleTextChange} onBlur={handleBlur} helperText={helperText} validation={validation} disabled={disabled} calendarIcon={CalendarIcon} calendarLabel="Open calendar" onCalendarPress={handleCalendarPress} calendarButtonRef={calendarButtonRef} id={id} aria-label={ariaLabel} aria-required={required ? true : undefined} />;
  return <Dropdown open={open}
  // The field only anchors the calendar; the trailing button opens it (and
  // gets focus back on close). Popover only reports closes (Esc / outside).
  onOpenChange={setOpen} anchor={field} returnFocusRef={calendarButtonRef} matchTriggerWidth={false} contentLabel={contentLabel} data-sentry-element="Dropdown" data-sentry-component="DatePicker" data-sentry-source-file="DatePicker.tsx">
      <Calendar value={value} focusedDate={focusedDate} onFocusedDateChange={setFocusedDate} open={open} onSelect={handleSelect} min={min} max={max} isDateDisabled={isDateDisabled} weekStartsOn={weekStartsOn} label={contentLabel} data-sentry-element="Calendar" data-sentry-source-file="DatePicker.tsx" />
    </Dropdown>;
};